import React from 'react'
import { Avatar, Box, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import { Add, Delete, EmojiEvents, Remove, Star } from '@mui/icons-material'
import type { CartItem as CartItemType } from '../../types/cart'
import { useCart } from '../../contexts/CartContext'

interface CartItemProps {
  item: CartItemType
}

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { updateQuantity, removeItem } = useCart()

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity <= 0) removeItem(item.id)
    else updateQuantity(item.id, newQuantity)
  }

  const subtotal = item.pacote.preco * item.quantity

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.05)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
        }}
      >
        <Avatar
          variant="rounded"
          src={item.pacote.urlImagem ?? undefined}
          alt={item.pacote.nome}
          sx={{ width: 60, height: 60, borderRadius: 2, border: '2px solid #00e676' }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={900} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.pacote.nome}
            </Typography>
            {item.pacote.vip && (
              <Chip
                icon={<Star sx={{ fontSize: 14 }} />}
                label="VIP"
                size="small"
                color="warning"
                sx={{ height: 20, bgcolor: 'rgba(255,215,0,0.2)', border: '1px solid rgba(255,215,0,0.5)' }}
              />
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {formatPrice(item.pacote.preco)} cada
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
            {item.pacote.pontos > 0 && (
              <Chip
                icon={<EmojiEvents sx={{ fontSize: 14 }} />}
                label={`${item.pacote.pontos} pts`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
            {item.pacote.warzoneCoin && <Chip size="small" label="Warzone Coin" color="warning" variant="outlined" sx={{ height: 20 }} />}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <IconButton size="small" onClick={() => handleQuantityChange(item.quantity - 1)} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}>
              <Remove fontSize="small" />
            </IconButton>
            <Chip label={item.quantity} size="small" sx={{ bgcolor: '#00e676', color: '#06110b', fontWeight: 900, minWidth: 40 }} />
            <IconButton size="small" onClick={() => handleQuantityChange(item.quantity + 1)} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}>
              <Add fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ textAlign: 'right', minWidth: 'fit-content' }}>
          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
            {formatPrice(subtotal)}
          </Typography>
          <IconButton size="small" onClick={() => removeItem(item.id)} sx={{ color: 'error.main' }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ opacity: 0.1, my: 1 }} />
    </Box>
  )
}

export default CartItem

